const io = require('socket.io-client');
const LangString = require('@/lib/Lang/LangString');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const TcpSocketConnection = require('@/libraries/DigiTickets/TcpSocketConnection');
const { timeoutPromise } = require('@/functions/functions');

/**
 * Handles communication with the "ProPoint Agent".
 *
 * The ProPoint Agent (formerly "socket proxy") is a standalone Node server running on the client's machine
 * (accessible at 127.0.0.1:8443 via a WebSocket).
 * It allows ProPoint to access resources it otherwise would be unable to due to running in a browser.
 * The Agent currently supports:
 * - Proxying connections to TCP sockets (for connecting to payment terminals)
 * - Writing to the filesystem.
 * - Printing
 *
 * We create a single WebSocket connection to the Agent on startup if necessary for the client's configuration.
 *
 * @param $timeout
 * @param {LangService} langService
 * @param {ToastFactory} toastFactory
 */
class AgentService {
    constructor(
        $timeout,
        langService,
        toastFactory
    ) {
        this.$timeout = $timeout;

        /**
         * @type {LangService}
         */
        this.langService = langService;

        /**
         * @type {ToastFactory}
         */
        this.toastFactory = toastFactory;

        /**
         * @type {ConsoleLogger}
         */
        this.log = logFactory('Agent', true);

        /**
         * The configuration options for the agent connection and their default values.
         *
         * These were previously stored as part of the payment method config (for Verifone or WorldPay).
         * They are hard coded here for the time being so they are separated from payment method configuration, as the
         * agent now does more than just connecting to payment terminals, and no clients have had a need to
         * override these default values.
         */
        this.config = {
            /**
             * IP / hostname of the host the agent is running on.
             * localhost.dtapps.co.uk points to 127.0.0.1
             *
             * @type {string}
             */
            agentHost: 'localhost.dtapps.co.uk',

            /**
             * Port the agent is listening on.
             *
             * @type {number}
             */
            agentPort: 8443,

            /**
             * Use a secure connection to the ProPoint agent?
             * true = https, false = http
             *
             * @type {boolean}
             */
            secureAgentConnection: true,

            /**
             * Enable extra console logs?
             *
             * @type {boolean}
             */
            verbose: true,
        };

        /**
         * Socket.io connection.
         *
         * @type {socket.io-client.Socket}
         */
        this.ioSocket = null;

        /**
         * @type {Object<TcpSocketConnection>}
         */
        this.tcpSockets = {};

        /**
         * Callback when the WebSocket begins connecting.
         *
         * @type {?Function}
         */
        this.onConnecting = null;

        /**
         * Callback when the WebSocket is connected.
         *
         * @type {?Function}
         */
        this.onConnected = null;

        /**
         * Callback when the WebSocket connection is closed.
         *
         * @type {?Function}
         */
        this.onClose = null;

        /**
         * Callback when an error message is received from the WebSocket.
         *
         * @type {?Function}
         */
        this.onError = null;

        /**
         * Info about the agent server (version etc.) received when first connecting.
         *
         * @type {?{agentVersion: string, agentStartedAt: Date}}
         */
        this.serverInfo = null;

        /**
         * Is a WebSocket connection to the agent established?
         * true = connected
         * false = not connected
         * null = in progress
         *
         * @type {?boolean}
         */
        this.connected = false;

        /**
         * If we're in the process of connecting to the agent this will be a promise.
         *
         * @type {Promise}
         */
        this.connectingPromise = null;

        /**
         * If true this will display a "failed to connect to agent" error when connection fails.
         * This should only be enabled if the agent is needed.
         *
         * @private
         * @type {boolean}
         */
        this.displayConnectionErrorToUser = false;

        /**
         * Why the agent connection is required (e.g. which payment method needs it).
         * Included in the error message shown when connection fails.
         *
         * @private
         * @type {?LangString}
         */
        this.requiredReason = null;

        /**
         * @type {number}
         */
        this.connectAttempts = 0;
    }

    /**
     * @param {boolean} displayConnectionErrorToUser
     * @param {?LangString} [requiredReason] Why the agent connection is required. Shown if the connection fails.
     * @return {Promise}
     */
    connectToAgent(displayConnectionErrorToUser = false, requiredReason = null) {
        // Enable displayConnectionErrorToUser if it was already enabled or should be enabled now.
        // It should never be disabled once enabled.
        this.displayConnectionErrorToUser = (this.displayConnectionErrorToUser || displayConnectionErrorToUser);

        if (requiredReason) {
            this.requiredReason = requiredReason;
        }

        if (this.connected) {
            this.log.debug('Already connected to Agent.');
            return Promise.resolve();
        }

        if (this.connectingPromise) {
            this.log.debug('Agent connection already in progress.');
            return this.connectingPromise;
        }

        this.connectingPromise = this.connectToAgentReal();
        ++this.connectAttempts;

        // Loading spinner is conditionally displayed.
        if (this.displayConnectionErrorToUser) {
            this.toastFactory.spinner(
                '',
                this.langService.getText('AGENT.TOAST_CONNECTING'),
                this.connectingPromise
            );
        }

        this.connectingPromise
            .then(() => {
                // Success is always displayed.
                this.toastFactory.success(
                    '',
                    this.langService.getText('AGENT.TOAST_CONNECTED')
                );
                this.connectAttempts = 0;
            })
            .catch((error) => {
                // This needs to wait a short while before showing because there might be a previous error message
                // that is still fading out.
                timeoutPromise(1000).then(() => {
                    this.log.debug('Connection attempts', this.connectAttempts);
                    if (this.displayConnectionErrorToUser && this.connectAttempts < 5) {
                        const message = (
                            this.requiredReason
                                ? this.getRequiredConnectionFailedMessage(this.requiredReason)
                                : this.langService.getText('AGENT.TOAST_CONNECTION_FAILED')
                        ) + ' ' + error.toString();

                        this.toastFactory.error(
                            'AGENT.AGENT_ERROR',
                            message,
                            {
                                buttonText: 'Retry',
                                onclick: () => {
                                    this.connectToAgent();
                                },
                            }
                        );
                    }
                });
            })
            .finally(() => {
                this.connectingPromise = null;
            });

        return this.connectingPromise;
    }

    /**
     * Establish a WebSocket connection to the proxy server.
     * This should not be called directly. Use the connectToAgent method instead, which is debounced.
     *
     * @return {Promise<any>}
     * @private
     */
    connectToAgentReal() {
        this.log.debug('Connecting to Agent');
        return new Promise((resolve, reject) => {
            const proxyScheme = this.config.secureAgentConnection ? 'https' : 'http';
            const proxyUrl = proxyScheme + '://' + this.config.agentHost + ':' + this.config.agentPort;

            let ioManager = new io.Manager(
                proxyUrl,
                {
                    transports: ['websocket'],
                    reconnection: false,
                    reconnectionAttempts: 1,
                }
            );

            this.ioSocket = ioManager.socket('/');
            this.connected = null;
            if (typeof this.onConnecting === 'function') {
                this.onConnecting();
            }

            // Failed to connect to the WebSocket.
            this.ioSocket.on(
                'connect_error',
                (error) => {
                    this.$timeout(() => {
                        this.log.error('Websocket connect_error', error);
                        reject(error);

                        if (typeof this.onError === 'function') {
                            this.onError(error.toString());
                        }
                        this.connected = false;
                        if (typeof this.onClose === 'function') {
                            this.onClose();
                        }
                    });
                }
            );

            // There was an error with the WebSocket.
            this.ioSocket.on(
                'error',
                (error) => {
                    this.$timeout(() => {
                        this.log.error('Websocket error', error);
                        if (typeof this.onError === 'function') {
                            this.onError(error.toString());
                        }
                    });
                }
            );

            // Setup listeners for data from the proxy server...

            // The agent server sent back some info about itself.
            this.ioSocket.on(
                'serverInfo',
                (data) => {
                    this.$timeout(() => {
                        this.log.debug('Server info received.', data);
                        this.serverInfo = data;

                        if (data && data.hasOwnProperty('agentStartedAt')) {
                            this.serverInfo.agentStartedAt = new Date(data.agentStartedAt);
                        }
                    });
                }
            );

            // Listen for incoming data.
            // Send it to the appropriate TCP socket object.
            this.ioSocket.on(
                'tcpSocketData',
                (data) => {
                    this.$timeout(() => {
                        if (data && data.socketName) {
                            if (this.tcpSockets.hasOwnProperty(data.socketName)) {
                                this.tcpSockets[data.socketName]._didReceiveData(data);
                            }
                        }
                    });
                }
            );

            // Handle (the proxy server telling us about) TCP socket errors.
            // Send it to the appropriate TCP socket object.
            this.ioSocket.on(
                'tcpSocketError',
                (data) => {
                    this.$timeout(() => {
                        this.log.error('TCP socket error.', data);
                        if (data && data.socketName) {
                            if (this.tcpSockets.hasOwnProperty(data.socketName)) {
                                this.tcpSockets[data.socketName]._didError(data);
                            }
                        }
                    });
                }
            );

            // Handle (the proxy server telling us about) TCP socket disconnections.
            // Tell the appropriate TCP socket object.
            this.ioSocket.on(
                'tcpSocketClosed',
                (data) => {
                    this.$timeout(() => {
                        this.log.debug('TCP socket closed.', data);
                        if (data && data.socketName) {
                            if (this.tcpSockets.hasOwnProperty(data.socketName)) {
                                this.tcpSockets[data.socketName]._didClose();
                            }
                        }
                    });
                }
            );

            // Handle WebSocket disconnection.
            this.ioSocket.on(
                'disconnect',
                () => {
                    this.$timeout(() => {
                        this.connected = false;

                        this.toastFactory.error(
                            'AGENT.AGENT_ERROR',
                            this.langService.getText('AGENT.TOAST_DISCONNECTED')
                        );

                        // Tell all of the TCP sockets they are disconnected.
                        // eslint-disable-next-line unused-imports/no-unused-vars
                        for (let socketName in this.tcpSockets) {
                            if (this.tcpSockets.hasOwnProperty(socketName)) {
                                this.tcpSockets[socketName]._didClose();
                            }
                        }
                        if (typeof this.onClose === 'function') {
                            this.onClose();
                        }
                    });
                }
            );

            // Handle WebSocket connection.
            this.ioSocket.on(
                'connect',
                (response) => {
                    this.$timeout(() => {
                        this.log.debug('Websocket connected.', response);
                        // Configure the proxy server.
                        // There is no longer anything to configure, so this is commented out.
                        // this.ioSocket.emit(
                        //     'configure',
                        //     {
                        //     }
                        // );
                        this.connected = true;
                        if (typeof this.onConnected === 'function') {
                            this.onConnected();
                        }
                        resolve();
                    });
                }
            );
        });
    }

    /**
     * @param name
     * @param host
     * @param port
     *
     * @return {TcpSocketConnection}
     */
    createTcpSocket(name, host, port) {
        let tcpSocket = new TcpSocketConnection(
            name,
            host,
            port,
            this
        );
        this.addTcpSocket(tcpSocket);

        return tcpSocket;
    }

    /**
     * @param {TcpSocketConnection} tcpSocket
     */
    addTcpSocket(tcpSocket) {
        this.tcpSockets[tcpSocket.name] = tcpSocket;
    }

    /**
     * Tell the proxy server to connect to the specified TCP socket.
     * This should not be called directly. Call connect() on a TCP Socket object instead.
     *
     * @param {TcpSocketConnection} socket
     *
     * @return {Promise<any>}
     */
    _connectTcpSocket(socket) {
        return this.connectToAgent()
            .then(() => new Promise((resolve, reject) => {
                this.log.debug('Connecting to TCP socket', socket.host + ':' + socket.port);

                socket._isConnecting();

                // Send a message over the Websocket telling the agent to connect to a TCP socket and keep the
                // connection alive.
                this.ioSocket.emit(
                    'connectTcpSocket',
                    {
                        socketName: socket.name,
                        host: socket.host,
                        port: socket.port,
                        encoding: socket.encoding,
                    },
                    (response) => {
                        this.$timeout(() => {
                            this.log.debug('TCP socket connect response.', response);
                            if (response.connected) {
                                socket._didConnect();
                                resolve(response);
                            } else {
                                socket._didError({ error: response.error });
                                reject(response);
                            }
                        });
                    }
                );
            }))
            .catch(() => {
                throw new Error('ProPoint Agent is not connected.');
            });
    }

    /**
     * @param {TcpSocketConnection} socket
     *
     * @returns {Promise}
     * @private
     */
    _disconnectTcpSocket(socket) {
        return new Promise((resolve, reject) => {
            this.ioSocket.emit(
                'disconnectTcpSocket',
                {
                    socketName: socket.name,
                },
                (response) => {
                    this.$timeout(() => {
                        if (response === true) {
                            resolve();
                        } else {
                            reject(new Error(response.error));
                        }
                    });
                }
            );
        });
    }

    /**
     * Tell the proxy server to send data to the specified TCP socket.
     * This should not be called directly. Call sendData() on a TCP Socket object instead.
     *
     * @param {TcpSocketConnection} socket
     * @param {string} dataString
     * @param {function} callback
     */
    _sendData(socket, dataString, callback) {
        this.ioSocket.emit(
            'sendTcpData',
            {
                socketName: socket.name,
                data: dataString,
            },
            (response) => {
                this.$timeout(() => {
                    if (typeof callback === 'function') {
                        callback(response);
                    }
                });
            }
        );
    }

    /**
     * The message displayed and logged when the agent is required but a connection could not be made.
     *
     * @param {LangString} reason
     *
     * @return {string}
     */
    getRequiredConnectionFailedMessage(reason) {
        return this.langService.getText(reason)
            + ' '
            + this.langService.getText('AGENT.REQUIRED_CONNECTION_FAILED');
    }

    /**
     * Determine if the company needs to use the Agent.
     * Returns the reason the Agent is needed as a LangString, or null if it is not needed.
     *
     * @param {PaymentMethod[]} paymentMethods
     *
     * @return {?LangString}
     */
    companyNeedsAgent(paymentMethods) {
        const requiredForPaymentMethods = [
            'card-verifone-chip-and-pin',
            'card-worldpay-chip-and-pin',
        ];

        const requiringPaymentMethod = paymentMethods.find(
            /** @type {PaymentMethod} */
            (pm) => requiredForPaymentMethods.indexOf(pm.ref) !== -1
        );

        if (!requiringPaymentMethod) {
            return null;
        }

        return new LangString(
            'AGENT.REQUIRED_BY_PAYMENT_METHOD',
            { paymentMethod: requiringPaymentMethod.name }
        );
    }

    /**
     * @param {string} html
     * @param {?string} [printerName]
     *
     * @return {Promise<any>}
     */
    print(html, printerName) {
        return this.connectToAgent()
            .then(
                () => new Promise((resolve, reject) => {
                    this.ioSocket.emit(
                        'print',
                        {
                            html,
                            printerName,
                        },
                        (response) => {
                            this.$timeout(() => {
                                if (response.success) {
                                    resolve();
                                } else {
                                    reject(response.error);
                                }
                            });
                        }
                    );
                })
            );
    }

    /**
     * @return {Promise<string[]>}
     */
    getPrinterNames() {
        return this.connectToAgent()
            .then(
                () => new Promise((resolve, reject) => {
                    this.ioSocket.emit(
                        'getPrinters',
                        {},
                        (response) => {
                            this.$timeout(() => {
                                if (response.hasOwnProperty('printers')) {
                                    resolve(response.printers);
                                } else {
                                    reject(response.error);
                                }
                            });
                        }
                    );
                })
            );
    }

    /**
     * @return {Promise<string[]>}
     */
    getServerInfo() {
        return this.connectToAgent()
            .then(() => new Promise((resolve, reject) => {
                this.ioSocket.emit(
                    'getServerInfo',
                    {},
                    (response) => {
                        this.$timeout(() => {
                            if (response) {
                                resolve(response);
                            } else {
                                reject();
                            }
                        });
                    }
                );
            }));
    }

    /**
     * Read the contents of a file from the propoint data directory.
     *
     * @param {string} path
     *
     * @return {Promise<string>}
     */
    readFileFromDataDir(path) {
        return this.connectToAgent()
            .then(() => new Promise((resolve, reject) => {
                this.ioSocket.emit(
                    'readFileFromDataDir',
                    {
                        path,
                    },
                    (response) => {
                        this.$timeout(() => {
                            if (response.data) {
                                resolve(response.data);
                            } else {
                                reject(response.error);
                            }
                        });
                    }
                );
            }));
    }

    /**
     * Store a file in the propoint data directory.
     *
     * @param {string} path
     * @param {string} data
     *
     * @return {Promise<boolean>}
     */
    writeFileToDataDir(path, data) {
        return this.connectToAgent()
            .then(() => new Promise((resolve, reject) => {
                this.ioSocket.emit(
                    'writeFileToDataDir',
                    {
                        path,
                        data,
                    },
                    (response) => {
                        this.$timeout(() => {
                            if (response.success) {
                                resolve(true);
                            } else {
                                reject(response.error);
                            }
                        });
                    }
                );
            }));
    }
}

module.exports = AgentService;
